<template>
  <div v-cloak ref="printArea" class="print-container">
    <div class="md-layout">
      <div class="md-layout-item md-size-100">
        <nav-tabs-card>
          <template slot="content">
            <span class="md-nav-tabs-title">Guidance:</span>
            <md-tabs class="md-info" md-alignment="left">

              <md-tab id="tab-dashboard" md-label="Dashboard" md-icon="dashboard">
                <div class="dashboard-container">
                  <md-card class="guidance-header-card">
                    <md-card-header class="md-card-header-icon md-card-header-green">
                      <div class="card-icon">
                        <md-icon class="md-size-2x">dashboard</md-icon>
                      </div>
                      <div class="guidance-title-section">
                        <h3 class="title">
                          Guidance <b>#<span>{{ guidance.guidance_number }}</span></b>
                          <badge :type="guidanceBadge(guidance.status)" class="status-badge">
                            {{ guidance.status || 'Draft' }}
                          </badge>
                        </h3>
                        <p class="guidance-name">{{ guidance.name }}</p>
                      </div>
                    </md-card-header>

                    <md-card-content>
                      <!-- Quick Info Chips -->
                      <div class="quick-stats">
                        <div class="stat-chip">
                          <md-icon>date_range</md-icon>
                          <span>{{ guidance.date_range }}</span>
                        </div>
                        <div class="stat-chip">
                          <md-icon>business</md-icon>
                          <span>{{ getPortName() }}</span>
                        </div>
                        <div class="stat-chip">
                          <md-icon>account_balance</md-icon>
                          <span>{{ getDepartmentName() }}</span>
                        </div>
                        <div class="stat-chip">
                          <md-icon>local_atm</md-icon>
                          <span>{{ getTariffName() }}</span>
                        </div>
                        <div class="stat-chip">
                          <md-icon>person</md-icon>
                          <span>Created by: {{ getCreatorName() }}</span>
                        </div>
                      </div>
                    </md-card-content>
                  </md-card>

                  <!-- Two Column Layout for Dashboard -->
                  <div class="md-layout">
                    <!-- Left Column: Statistics -->
                    <div class="md-layout-item md-size-70">
                      <!-- Key Metrics Cards -->
                      <div class="key-metrics">
                        <div class="metric-card">
                          <div class="metric-icon">
                            <md-icon class="md-primary">people</md-icon>
                          </div>
                          <div class="metric-content">
                            <div class="metric-value">{{ formatNumber(getTotalLabourers()) }}</div>
                            <div class="metric-label">Total Labourers (Man-Days)</div>
                          </div>
                        </div>
                        <div class="metric-card">
                          <div class="metric-icon">
                            <md-icon class="md-accent">schedule</md-icon>
                          </div>
                          <div class="metric-content">
                            <div class="metric-value">{{ formatHours(getTotalHours()) }}</div>
                            <div class="metric-label">Total Hours</div>
                          </div>
                        </div>
                        <div class="metric-card">
                          <div class="metric-icon">
                            <md-icon class="md-success">attach_money</md-icon>
                          </div>
                          <div class="metric-content">
                            <div class="metric-value">{{ formatCurrency(getTotalAmount()) }}</div>
                            <div class="metric-label">Total Amount</div>
                          </div>
                        </div>
                        <div class="metric-card">
                          <div class="metric-icon">
                            <md-icon class="md-warning">warning</md-icon>
                          </div>
                          <div class="metric-content">
                            <div class="metric-value">{{ formatCurrency(getExceptionAmount()) }}</div>
                            <div class="metric-label">Weekend Amount</div>
                          </div>
                        </div>
                      </div>

                      <md-card class="statistics-card">
                        <md-card-header>
                          <div class="card-header-title">
                            <md-icon>bar_chart</md-icon>
                            <span>Labour Distribution by Day Type</span>
                          </div>
                        </md-card-header>
                        <md-card-content>
                          <div class="day-type-stats">
                            <div v-for="item in dayTypeBreakdown" :key="item.dayType" class="day-type-card"
                                 :class="getDayTypeClass(item.dayType)">
                              <div class="day-type-header">{{ item.dayType }}</div>
                              <div class="day-type-value">{{ formatNumber(item.total_labourers) }}</div>
                              <div class="day-type-label">Labourers</div>
                              <div class="day-type-sub">{{ item.days }} days</div>
                            </div>
                          </div>
                        </md-card-content>
                      </md-card>

                      <guidance-statistics-panel
                          v-if="workPoints.length > 0"
                          :guidance="guidance"
                          :work-points="workPoints"
                      />
                    </div>

                    <div class="md-layout-item md-size-30">
                      <div class="quick-stats">
                        <div class="stat-chip">
                          <md-button class="md-icon-button md-raised  md-warning m-2"
                                     @click="goToEdit(guidance.id)">
                            <md-icon>edit</md-icon>
                          </md-button>
                        </div>
                        <div class="stat-chip">

                          <md-button class="md-icon-button md-raised  md-danger m-2"
                                     @click="goToStop(guidance.id)">
                            <md-icon>gpp_bad</md-icon>
                          </md-button>
                        </div>
                        <div class="stat-chip md-info">

                          <md-button @click="goToCopy(guidance.id)" class="md-icon-button md-raised md-info">
                            <md-icon class="mr-1">content_copy</md-icon>
                          </md-button>
                        </div>
                        <div class="stat-chip md-primary">
                          <md-button @click="goBack" class="md-icon-button md-raised md-primary ">
                            <md-icon>arrow_back</md-icon>
                          </md-button>
                        </div>
                      </div>
                      <!-- Status Progress -->
                      <md-card class="status-card">
                        <md-card-header>
                          <div class="card-header-title">
                            <md-icon>timeline</md-icon>
                            <span>Status Progress</span>
                          </div>
                        </md-card-header>
                        <md-card-content>
                          <div class="status-progress">
                            <div v-for="step in statusSteps" :key="step.name" class="progress-step"
                                 :class="getProgressStepClass(step.name)">
                              <div class="step-marker">
                                <md-icon v-if="isStatusCompleted(step.name)">check_circle</md-icon>
                                <md-icon v-else-if="guidance.status === step.name">radio_button_checked</md-icon>
                                <md-icon v-else>radio_button_unchecked</md-icon>
                              </div>
                              <div class="step-content">
                                <div class="step-name">{{ step.name }}</div>
                                <div class="step-date" v-if="getStatusDate(step.name)">
                                  {{ formatDateShort(getStatusDate(step.name)) }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </md-card-content>
                      </md-card>

                      <!-- Status History -->
                      <md-card class="history-card">
                        <md-card-header>
                          <div class="card-header-title">
                            <md-icon>history</md-icon>
                            <span>Status History</span>
                          </div>
                        </md-card-header>
                        <md-card-content>
                          <div class="status-history" v-if="canListStatus()">
                            <div v-for="statusItem in guidance.statuses_with_trashed" :key="statusItem.id"
                                 class="history-item">
                              <div class="history-badge"
                                   :class="statusItem.deleted_at === null ? 'badge-info' : 'badge-danger'">
                                {{ statusItem.name }}
                              </div>
                              <div class="history-details">
                                <div class="history-reason">{{ statusItem.reason || 'No reason provided' }}</div>
                                <div class="history-meta">
                                  <span v-html="creatorLabel(statusItem)"></span>
                                  <span class="history-date">{{ formatDateTime(statusItem.created_at) }}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-else class="no-history">
                            <md-icon>info</md-icon>
                            <p>No status history available</p>
                          </div>
                        </md-card-content>
                      </md-card>

                      <md-card class="actions-card">
                        <md-card-header>
                          <div class="card-header-title">
                            <md-icon>build</md-icon>
                            <span>Actions</span>
                          </div>
                        </md-card-header>
                        <md-card-content>
                          <div class="action-buttons-vertical">
                            <md-button @click="goManageItem(guidance.id)" class="md-primary md-block" v-if="canEdit() && guidance.guidance_type==='Per Man Hour' ">
                              <md-icon>edit</md-icon>
                              Manage Guidance Item
                            </md-button>
                            <md-button @click="goManageWorkItem(guidance.id)" class="md-primary md-block" v-if="canEdit() && guidance.guidance_type==='Per Work Done' ">
                              <md-icon>edit</md-icon>
                              Manage Guidance Work Item
                            </md-button>
                            <md-button v-if="canAssignLabourer()" @click="goLabourerAssignment(guidance.id)"
                                       class="md-primary md-block">
                              <md-icon>assignment_ind</md-icon>
                              Labourer Assignment
                            </md-button>
                            <md-button v-for="status in visibleStatusButtons" :key="status.id"
                                       class="md-info md-block"
                                       @click="showStatusModal(status)">
                              <md-icon>check_circle</md-icon>
                              {{ status.action }}
                            </md-button>
                            <md-button class="md-danger md-block" @click="showRejectModal" v-if="canReject()">
                              <md-icon>cancel</md-icon>
                              Reject
                            </md-button>
                          </div>
                        </md-card-content>
                      </md-card>
                    </div>
                  </div>
                </div>
              </md-tab>

              <md-tab id="tab-workpoints" md-label="Work Points" md-icon="business">
                <div class="workpoints-container">
                  <div v-for="(workplace, index) in workplaces" :key="index" class="workpoint-section">
                    <md-card class="workpoint-card">
                      <md-card-header class="workpoint-header">
                        <div class="workpoint-icon">
                          <md-icon>location_city</md-icon>
                        </div>
                        <h4 class="workpoint-title">{{ getWorkplaceName(workplace) }}</h4>
                        <div class="workpoint-stats">
                          <span class="stat-badge">
                            <md-icon>people</md-icon> {{ workplace.labourer_count }}
                          </span>
                        </div>
                      </md-card-header>

                      <md-card-content>
                        <md-table v-if="workplace.activities && workplace.activities.length">
                          <md-table-row>
                            <md-table-head md-numeric>#</md-table-head>
                            <md-table-head>Activity</md-table-head>
                            <md-table-head v-for="shift in selected_shifts" :key="shift.id" md-numeric>
                              {{ shift.name }}
                            </md-table-head>
                            <md-table-head md-numeric>Total</md-table-head>
                          </md-table-row>

                          <md-table-row v-for="(activity, idx) in workplace.activities" :key="idx">
                            <md-table-cell md-numeric>
                              <md-icon v-if="hasExceptions(activity)" class="exception-icon-small">priority_high
                              </md-icon>
                              <span v-else>{{ idx + 1 }}</span>
                            </md-table-cell>
                            <md-table-cell>{{ getActivityName(activity) }}</md-table-cell>
                            <md-table-cell v-for="shift in selected_shifts" :key="shift.id" md-numeric>
                              {{ getShiftLabourerCount(activity, shift.id) }}
                            </md-table-cell>
                            <md-table-cell md-numeric>
                              <strong>{{ activity.labourer_count || 0 }}</strong>
                            </md-table-cell>
                          </md-table-row>

                          <md-table-row class="subtotal-row">
                            <md-table-cell></md-table-cell>
                            <md-table-cell><strong>Sub Total</strong></md-table-cell>
                            <md-table-cell v-for="(shift, idx2) in selected_shifts" :key="idx2" md-numeric>
                              <strong>{{ getWorkplaceShiftTotal(workplace, shift.id) }}</strong>
                            </md-table-cell>
                            <md-table-cell md-numeric>
                              <strong>{{ workplace.labourer_count }}</strong>
                            </md-table-cell>
                          </md-table-row>
                        </md-table>

                        <div v-else class="empty-activities">
                          <md-icon>info</md-icon>
                          <p>No activities found for this work point</p>
                        </div>
                      </md-card-content>
                    </md-card>
                  </div>
                </div>
              </md-tab>

              <md-tab id="tab-schedules" md-label="Schedules" md-icon="event">
                <guidance-schedule-table
                    v-if="guidance.id"
                    :tableData="guidance.guidanceSchedules"
                    :guidance="guidance"
                    @removeRole="deleteScheduleRow"
                    @addRoleToUser="showScheduleModal"
                    @searchUserTable="searchOnScheduleTables"
                    @generateSchedules="generateSchedule"
                    @reRunAssignment="reAssignSchedule"
                />
              </md-tab>

              <md-tab id="tab-takeons" md-label="Takeons" md-icon="confirmation_number">
                <guidance-takeon-table
                    v-if="guidance.id"
                    :guidance="guidance"
                />
              </md-tab>

              <!-- TAB 5: Exceptions -->
              <md-tab id="tab-exceptions" md-label="Weekends" md-icon="warning">
                <div class="exceptions-summary">
                  <div v-if="hasSaturdayExceptions" class="exception-group">
                    <h4>
                      <md-icon>event</md-icon>
                      Saturday Exceptions
                    </h4>
                    <md-table>
                      <md-table-row>
                        <md-table-head>Work Point</md-table-head>
                        <md-table-head>Activity</md-table-head>
                        <md-table-head md-numeric>Labourers</md-table-head>
                        <md-table-head></md-table-head>

                      </md-table-row>
                      <md-table-row v-for="exc in saturdayExceptions" :key="exc.id">
                        <md-table-cell>{{ exc.workplace }}</md-table-cell>
                        <md-table-cell>{{ exc.activity }}</md-table-cell>
                        <md-table-cell md-numeric>{{ exc.total_labourer_count }}</md-table-cell>
                        <md-table-cell md-numeric>
                          <div v-for="shift in exc.shifts"
                               :key="shift.activity_shift_id">
                            <badge v-if="shift.labourer_count>0"
                                   type="info">
                              {{ shift.shift }} : {{ shift.labourer_count }}
                            </badge>
                          </div>
                        </md-table-cell>
                      </md-table-row>
                    </md-table>
                  </div>

                  <div v-if="hasSundayExceptions" class="exception-group">
                    <h4>
                      <md-icon>event</md-icon>
                      Sunday Exceptions
                    </h4>
                    <md-table>
                      <md-table-row>
                        <md-table-head>Work Point</md-table-head>
                        <md-table-head>Activity</md-table-head>
                        <md-table-head md-numeric>Labourers</md-table-head>
                        <md-table-head></md-table-head>
                      </md-table-row>
                      <md-table-row v-for="exc in sundayExceptions" :key="exc.id">
                        <md-table-cell>{{ exc.workplace }}</md-table-cell>
                        <md-table-cell>{{ exc.activity }}</md-table-cell>
                        <md-table-cell md-numeric>{{ exc.total_labourer_count }}</md-table-cell>
                        <md-table-cell md-numeric>
                          <div v-for="shift in exc.shifts"
                               :key="shift.activity_shift_id">
                            <badge v-if="shift.labourer_count>0"
                                   type="info">
                              {{ shift.shift }} : {{ shift.labourer_count }}
                            </badge>
                          </div>
                        </md-table-cell>
                      </md-table-row>
                    </md-table>
                  </div>

                  <div v-if="hasHolidayExceptions" class="exception-group">
                    <h4>
                      <md-icon>holiday_village</md-icon>
                      Holiday Exceptions
                    </h4>
                    <md-table>
                      <md-table-row>
                        <md-table-head>Work Point</md-table-head>
                        <md-table-head>Activity</md-table-head>
                        <md-table-head md-numeric>Labourers</md-table-head>
                        <md-table-head md-numeric></md-table-head>
                      </md-table-row>
                      <md-table-row v-for="exc in holidayExceptions" :key="exc.id">
                        <md-table-cell>{{ exc.workplace }}</md-table-cell>
                        <md-table-cell>{{ exc.activity }}</md-table-cell>
                        <md-table-cell md-numeric>{{ exc.total_labourer_count }}</md-table-cell>
                        <md-table-cell md-numeric>
                          <div v-for="shift in exc.shifts"
                               :key="shift.activity_shift_id">
                            <badge v-if="shift.labourer_count>0"
                                   type="info">
                              {{ shift.shift }} : {{ shift.labourer_count }}
                            </badge>
                          </div>
                        </md-table-cell>
                      </md-table-row>
                    </md-table>
                  </div>

                  <div v-if="!hasAnyExceptions" class="no-exceptions">
                    <md-icon>check_circle</md-icon>
                    <p>No Weekend configured for this guidance</p>
                  </div>
                </div>
              </md-tab>
              <md-tab id="tab-rediness" md-label="Readness" md-icon="tick">
                <guidance-readiness v-if="guidance.id"
                    :guidance-id="guidance.id"/>
              </md-tab>

            </md-tabs>
          </template>
        </nav-tabs-card>
      </div>
    </div>

    <!-- Status Change Modal -->
    <modal v-if="modal.classicModal" @close="classicModalHide">
      <template slot="header">
        <h4 class="modal-title">Change Status to {{ getSelectedAction() }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="classicModalHide">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <div slot="body">
        <div v-if="reject">
          <p>Please provide reason(s) for rejection</p>
          <form @submit.prevent="updateStatus">
            <md-field>
              <md-input v-model="status.reason" placeholder="Reasons for Rejection"/>
              <validation-error :errors="apiValidationErrors.reason"/>
            </md-field>
            <md-button type="submit" class="md-danger md-dense">Reject</md-button>
          </form>
        </div>
        <div v-else>
          <p class="font-weight-bold">Ensure that the Guidance is correct then state reason for {{
              getSelectedAction()
            }}</p>
          <form @submit.prevent="updateStatus">
            <md-field>
              <md-input v-model="status.reason" placeholder="Reason (Optional)"/>
              <validation-error :errors="apiValidationErrors.reason"/>
            </md-field>
            <md-button type="submit" class="md-primary md-dense">
              {{ getSelectedAction() }}
            </md-button>
          </form>
        </div>
      </div>

      <template slot="footer">
        <md-button class="md-danger md-simple" @click="classicModalHide">Close</md-button>
      </template>
    </modal>

    <md-button @click="print" class="md-primary md-simple print-btn">Print</md-button>
    <guidance-pdf-actions
        v-if="guidance.id"
        :guidance-id="guidance.id"
        :guidance-number="guidance.guidance_number"
        :can-download="true"
        :can-preview="true"
        :can-regenerate="canEdit()"
        :can-print="true"
    />
  </div>
</template>

<script>
import formMixin from '@/mixins/form-mixin';
import badgeMixin from '@/mixins/badge-mixin';
import guidanceStatisticsMixin from '@/mixins/guidance-statistics-mixin';
import {Modal, NavTabsCard, ValidationError} from '@/components';
import Vuex from 'vuex';
import Swal from 'sweetalert2';
import GuidanceScheduleTable from '@/components/Tables/GuidanceScheduleTable.vue';
import GuidanceTakeonTable from '@/pages/settings/guidances/components/GuidanceTakeonTable.vue';
import GuidanceStatisticsPanel from '@/pages/settings/guidances/components/GuidanceStatisticPanel.vue';
import Badge from '@/components/Badge.vue';
import GuidancePdfActions from '@/components/GuidancePdfActions.vue';
import GuidanceReadiness from "@/pages/settings/guidances/components/GuidanceReadiness.vue";

export default {
  components: {
    GuidanceReadiness,
    Badge,
    GuidanceTakeonTable,
    GuidanceScheduleTable,
    GuidanceStatisticsPanel,
    // TimeLine,
    // TimeLineItem,
    ValidationError,
    Modal,
    NavTabsCard,
    GuidancePdfActions
  },
  mixins: [formMixin, badgeMixin, guidanceStatisticsMixin],
  data() {
    return {
      modal: {
        classicModal: false,
      },
      selected_shifts: [],
      roles: [],
      allPermissions: [],
      workplaces: [],
      workPoints: [],
      guidance: {
        id: null,
        name: '',
        guidance_number: '',
        date_range: '',
        status: '',
        port: null,
        department: null,
        vendor: null,
        tariff: null,
        creator: null,
        statuses: [],
        statuses_with_trashed: [],
        possibles: [],
        guidanceSchedules: [],
        guidanceLabourerSchedules: [],
        saturday_exception_activity_guidances: [],
        sunday_exception_activity_guidances: [],
        holiday_exception_activity_guidances: []
      },
      status: {
        name: null,
        reason: null,
      },
      reject: false,
      selectedStatus: null,
    };
  },

  computed: {
    ...Vuex.mapState({
      me: state => state.profile.me
    }),

    statusSteps: function () {
      return [
        {name: 'Created', order: 1},
        {name: 'Reviewed', order: 2},
        {name: 'Certified', order: 3},
        {name: 'Confirmed', order: 4},
        {name: 'Approved', order: 5},
        {name: 'Accepted', order: 6}
      ];
    },

    saturdayExceptions: function () {
      return this.guidance.saturday_exception_activity_guidances || [];
    },

    sundayExceptions: function () {
      return this.guidance.sunday_exception_activity_guidances || [];
    },

    holidayExceptions: function () {
      return this.guidance.holiday_exception_activity_guidances || [];
    },

    hasSaturdayExceptions: function () {
      return this.saturdayExceptions.length > 0;
    },

    hasSundayExceptions: function () {
      return this.sundayExceptions.length > 0;
    },

    hasHolidayExceptions: function () {
      return this.holidayExceptions.length > 0;
    },

    hasAnyExceptions: function () {
      return this.hasSaturdayExceptions || this.hasSundayExceptions || this.hasHolidayExceptions;
    },

    hasActivities: function () {
      return this.workplaces.length > 0 &&
          this.workplaces[0] &&
          this.workplaces[0].activities &&
          this.workplaces[0].activities.length > 0;
    },

    visibleStatusButtons: function () {
      var self = this;
      var possibles = this.guidance.possibles || [];
      var visible = [];

      possibles.forEach(function (status, index) {
        if (self.canShowStatusButton(status, index)) {
          visible.push(status);
        }
      });

      return visible;
    },

    dayTypeBreakdown: function () {
      var stats = this.guidanceStatistics;
      if (!stats || !stats.by_day_type) return [];

      return [
        {
          dayType: 'Weekday',
          days: stats.by_day_type.Weekday.days || 0,
          total_labourers: stats.by_day_type.Weekday.labourer_count || 0,
          total_hours: stats.by_day_type.Weekday.total_hours || 0,
          total_amount: stats.by_day_type.Weekday.total_amount || 0
        },
        {
          dayType: 'Saturday',
          days: stats.by_day_type.Saturday.days || 0,
          total_labourers: stats.by_day_type.Saturday.labourer_count || 0,
          total_hours: stats.by_day_type.Saturday.total_hours || 0,
          total_amount: stats.by_day_type.Saturday.total_amount || 0
        },
        {
          dayType: 'Sunday',
          days: stats.by_day_type.Sunday.days || 0,
          total_labourers: stats.by_day_type.Sunday.labourer_count || 0,
          total_hours: stats.by_day_type.Sunday.total_hours || 0,
          total_amount: stats.by_day_type.Sunday.total_amount || 0
        },
        {
          dayType: 'Holiday',
          days: stats.holiday_count || 0,
          total_labourers: stats.by_day_type.Holiday ? stats.by_day_type.Holiday.labourer_count : 0,
          total_hours: stats.by_day_type.Holiday ? stats.by_day_type.Holiday.total_hours : 0,
          total_amount: stats.by_day_type.Holiday ? stats.by_day_type.Holiday.total_amount : 0
        }
      ];
    }
  },

  created: function () {
    this.get();
  },

  methods: {
    async get() {
      var self = this;
      try {

        var params = {
          id: this.$route.params.id,
          include: 'port,tariff,department,vendor,guidanceCategory,guidanceSchedules,' +
              'workItems,workItems.workplace,statuses,statuses.creator,workItems.workDetails,' +
              'workItems.workDetails.workItem,workItems.activityTariff,workItems.activityTariff.activity,creator,' +
              'creator.roles,creator.model,' +
              'guidanceWorkplaces.workplace.activities,' +
              'guidanceWorkplaces.activityGuidances.activity,' +
              'guidanceWorkplaces.activityGuidances.exceptions,' +
              'guidanceWorkplaces.activityGuidances.activityShifts.shift,' +
              'guidanceWorkplaces.activityGuidances.activityTariff,' +
              'guidanceWorkplaces.activityGuidances.exceptions.activityTariff,' +
              'guidanceWorkplaces.activityGuidances.exceptions.activity,' +
              'guidanceWorkplaces.activityGuidances.exceptions.activityShifts.shift'
        };
        await this.$store.dispatch('guidances/view', params);
        this.guidance = this.$store.getters['guidances/guidance'];
        if (this.guidance.guidance_type === 'Per Work Done') {
          self.goManageWorkItem(this.guidance.id);
        }
        self.workplaces = self.guidance.guidanceWorkplaces;
        console.log('self.workplaces', self.workplaces);
        self.workplaceTotals();
        console.log('updated.workplaces', self.workplaces);
        self.workPoints = self.workplaces;

        // await this.$store.dispatch('guidances/workplaces', this.guidance.id).then(function() {
        //
        // });

        var shiftParams = {filter: {guidance: this.guidance.id}};
        await this.$store.dispatch('shifts/list', shiftParams).then(function () {
          self.selected_shifts = self.$store.getters['shifts/list'];
          self.shiftTotals();
        });

      } catch (e) {
        console.error("Error loading guidance:", e);
        this.notifyException(e);
      }
    },

    getPortName: function () {
      return this.guidance.port ? this.guidance.port.name : 'N/A';
    },

    getDepartmentName: function () {
      return this.guidance.department ? this.guidance.department.name : 'N/A';
    },

    getTariffName: function () {
      return this.guidance.tariff ? this.guidance.tariff.name : 'N/A';
    },

    getCreatorName: function () {
      if (!this.guidance.creator) return 'N/A';
      var creator = this.guidance.creator;

      if (creator.model) {
        var firstName = creator.model.first_name || '';
        var lastName = creator.model.last_name || '';
        return (firstName + ' ' + lastName).trim() || creator.name || 'N/A';
      }
      return creator.name || 'N/A';
    },

    getWorkplaceName: function (workplace) {
      return workplace.workplace ? workplace.workplace.name : 'Unknown';
    },

    getActivityName: function (activity) {
      return activity.activity ? activity.activity.name : 'Unknown';
    },

    getShiftLabourerCount: function (activity, shiftId) {
      if (!activity.activity_shifts) return 0;
      var shiftData = activity.activity_shifts.find(function (s) {
        return s.shift && s.shift.id === shiftId;
      });
      return shiftData ? shiftData.labourer_count : 0;
    },

    getWorkplaceShiftTotal: function (workplace, shiftId) {
      var self = this;
      var total = 0;
      if (workplace.activities) {
        workplace.activities.forEach(function (activity) {
          total += self.getShiftLabourerCount(activity, shiftId);
        });
      }
      return total;
    },

    hasExceptions: function (activity) {
      return activity.exceptions && activity.exceptions.length > 0;
    },

    workplaceTotals: function () {
      var self = this;
      self.workplaces.forEach(function (workPoint) {
        workPoint.labourer_count = 0;
        if (workPoint.activityGuidances) {
          workPoint.activities = workPoint.activityGuidances;
          workPoint.activities.forEach(function (activity) {
            if (activity.activityShifts) {
              activity.activity_shifts = activity.activityShifts;
              activity.labourer_count = activity.activity_shifts.reduce(function (sum, shift) {
                return sum + (parseInt(shift.labourer_count) || 0);
              }, 0);
            } else {
              activity.labourer_count = 0;
            }
            workPoint.labourer_count += activity.labourer_count;
          });
        }
      });
    },

    shiftTotals: function () {
      var self = this;
      this.selected_shifts.forEach(function (shift) {
        self.workplaces.forEach(function (workplace) {
          if (!workplace.shifts) workplace.shifts = [];
          var sum = 0;
          if (workplace.activities) {
            workplace.activities.forEach(function (activity) {
              if (activity.activity_shifts) {
                activity.activity_shifts.forEach(function (actShift) {
                  if (actShift.shift && actShift.shift.id === shift.id) {
                    sum += parseInt(actShift.labourer_count) || 0;
                  }
                });
              }
            });
          }
          workplace.shifts.push(sum);
        });
      });
    },

    // Statistics helpers
    getTotalLabourers: function () {
      var stats = this.guidanceStatistics;
      return stats ? stats.labourer_count : 0;
    },

    getTotalHours: function () {
      var stats = this.guidanceStatistics;
      return stats ? stats.total_hours : 0;
    },

    getTotalAmount: function () {
      var stats = this.guidanceStatistics;
      return stats ? stats.total_amount : 0;
    },

    getExceptionAmount: function () {
      var stats = this.guidanceStatistics;
      return stats ? stats.exception_amount : 0;
    },

    // Status helpers
    isStatusCompleted: function (statusName) {
      if (!this.guidance.statuses) return false;
      return this.guidance.statuses.some(function (s) {
        return s.name === statusName;
      });
    },

    getStatusDate: function (statusName) {
      if (!this.guidance.statuses) return null;
      var status = this.guidance.statuses.find(function (s) {
        return s.name === statusName;
      });
      return status ? status.created_at : null;
    },

    getProgressStepClass: function (stepName) {
      if (this.isStatusCompleted(stepName)) return 'completed';
      if (this.guidance.status === stepName) return 'active';
      return 'pending';
    },

    getDayTypeClass: function (dayType) {
      var classes = {
        'Weekday': 'weekday-card',
        'Saturday': 'weekend-card',
        'Sunday': 'weekend-card',
        'Holiday': 'holiday-card'
      };
      return classes[dayType] || '';
    },

    canEdit: function () {
      return this.guidance.status !== 'Approved' && this.guidance.status !== 'Accepted';
    },

    canAssignLabourer: function () {
      return this.guidance.status === 'Accepted' && this.can('update-labourer-schedule');
    },

    canShowStatusButton: function (status, index) {
      if (index === 0) {
        return this.can('create-guidance-status') &&
            this.hasActivities &&
            (this.guidance.status === '' || this.guidance.status === 'Rejected' || this.guidance.status === 'New');
      }
      var prevStatus = this.guidance.possibles[index - 1];
      return this.can(status.action.toLowerCase() + '-guidance-status') &&
          this.hasActivities &&
          this.guidance.status === (prevStatus ? prevStatus.name : null);
    },

    userModelType: function () {
      return this.me ? this.me.model_type : null;
    },

    canReject: function () {
      if (this.userModelType() === 'Employee') {
        return (this.can('reject-guidance-status') &&
            this.guidance.guidanceSchedules.length === 0)
        || (this.can("delete-guidance-status"))
      }
      return (
          this.can('reject-guidance-status') &&
          this.guidance.status !== '' &&
          this.guidance.status !== 'Rejected' &&
          this.guidance.status !== 'Approved' &&
          this.guidance.status !== 'Accepted'
      ) || (
          this.roles.includes('Administrative Officer-PAM') &&
          this.guidance.status !== 'Accepted' &&
          this.guidance.status !== ''
      ) || (this.can("delete-guidance-status"));

    },

    canListStatus: function () {
      return this.can('list-guidance-status');
    },

    can: function (permission) {
      return this.allPermissions.includes(permission);
    },

    getSelectedAction: function () {
      return this.selectedStatus ? this.selectedStatus.action : (this.reject ? 'Reject' : 'Submit');
    },

    showStatusModal: function (status) {
      this.selectedStatus = status;
      this.reject = false;
      this.status.name = status.name;
      this.modal.classicModal = true;
    },

    showRejectModal: function () {
      this.selectedStatus = null;
      this.reject = true;
      this.modal.classicModal = true;
    },

    async updateStatus() {
      try {
        var guidanceData = {
          type: 'guidances',
          id: this.guidance.id,
          status: {
            name: this.status.name,
            reason: this.status.reason,
            reject: this.reject
          },
        };
        await this.$store.dispatch('guidances/update1', guidanceData);
        await this.$store.dispatch('alerts/success', 'Guidance updated successfully.');
        this.classicModalHide();
        await this.$store.dispatch(
            'notifications/pending'
        );

        this.get();
        //this.$router.go();
      } catch (e) {
        if (e.response && e.response.data && e.response.data.errors && e.response.data.errors[0]) {
          await this.$store.dispatch('alerts/error', e.response.data.errors[0].detail);
          this.setApiValidation(e.response.data.errors);
        } else {
          this.notifyException(e);
        }
      }
    },

    classicModalHide: function () {
      this.modal.classicModal = false;
      this.reject = false;
      this.selectedStatus = null;
      this.status.reason = null;
    },

    creatorLabel: function (status) {
      if (!status) return '';
      var creator = status.creator;
      if (!creator) return status.creator_id || '';
      var model = creator.model;
      if (model) {
        var firstName = typeof model.first_name === 'string' ? model.first_name.trim() : '';
        var lastName = typeof model.last_name === 'string' ? model.last_name.trim() : '';
        var checkNo = model.check_no ? '<br>(C/N: ' + model.check_no + ')' : '';
        var fullName = [firstName, lastName].filter(Boolean).join(' ');
        if (fullName) return fullName + checkNo;
        if (checkNo) return 'Unknown User' + checkNo;
        return creator.name || 'Unknown User';
      }
      return creator.name || 'Unknown User';
    },

    formatNumber: function (number) {
      if (!number) return '0';
      return new Intl.NumberFormat('en-US').format(number);
    },

    formatHours: function (hours) {
      if (!hours) return '0 hrs';
      return hours.toFixed(1) + ' hrs';
    },

    formatCurrency: function (amount) {
      if (!amount) return 'TZS 0';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    },

    formatDateShort: function (dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString();
    },

    formatDateTime: function (dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleString();
    },

    print: function () {
      var printContents = this.$refs.printArea.innerHTML;
      var originalContents = document.body.innerHTML;
      document.body.innerHTML = printContents;
      window.print();
      document.body.innerHTML = originalContents;
      window.location.reload();
    },

    goBack: function () {
      this.$router.push({name: 'Guidances List'});
    },

    goManageItem: function (id) {
      this.$router.push({name: 'Manage Guidance Item', params: {id: id}});
    },
    goManageWorkItem: function (id) {
      this.$router.push({name: 'Manage Guidance Work Item', params: {id: id}});
    },

    goLabourerAssignment: function (id) {
      this.$router.push({name: 'Guidance Labour Assignment', params: {id: id}});
    },

    deleteScheduleRow: function () {
      this.notifyInfo('Schedule cannot be deleted');
    },

    showScheduleModal: function () {
      this.notifyInfo('Schedule cannot be displayed from here');
    },

    searchOnScheduleTables: function () {
      this.notifyInfo('Schedule cannot be searched from here');
    },

    async generateSchedule(item) {
      try {
        await this.$store.dispatch('guidances/generateSchedule', item.id);
        this.notifyInfo('Request to generate schedules sent.');
      } catch (e) {
        this.notifyException(e);
      }
    },

    async reAssignSchedule(item) {
      try {
        await this.$store.dispatch('schedules/reRunAssignment', item);
        let result = this.$store.getters['schedules/schedule'];
        var message = result.message;
        this.notifyInfo(`Request to run assignment sent. ${message}`);
      } catch (e) {
        this.notifyException(e);
      }
    },
    goToEdit(id) {
      this.$router.push({name: 'Edit Guidance', params: {id}});
    },
    goToCopy() {
      Swal.fire({
        title: 'coming soon!!',
        type: 'info',
        icon: 'info',
        showCancelButton: true,
        confirmButtonClass: 'md-button md-success',
        cancelButtonClass: 'md-button md-danger',
      });
      // plan
      // show dialog to confirm action. are you sure!? you try to stop this guidance. this action will stop any future schedule and takeon
      // if user accept. then show a dialog/modal with input to enter stoping date.
      // after enter date. validate entered date then asking user for final confirmation of stop. if agreed dispatch the request
    },

    goToStop() {
      Swal.fire({
        title: 'coming soon!!',
        type: 'info',
        icon: 'info',
        showCancelButton: true,
        confirmButtonClass: 'md-button md-success',
        cancelButtonClass: 'md-button md-danger',
      });
      // plan
      // show dialog to confirm action. are you sure!? you try to stop this guidance. this action will stop any future schedule and takeon
      // if user accept. then show a dialog/modal with input to enter stoping date.
      // after enter date. validate entered date then asking user for final confirmation of stop. if agreed dispatch the request
    },
  },

  watch: {
    me: {
      handler: function (val) {

        if (!val) {
          return;
        }

        if (Array.isArray(val.roles)) {
          this.roles = val.roles.map(function (r) {
            return r.name;
          });
        }

        if (Array.isArray(val.allPermissions)) {
          this.allPermissions = val.allPermissions.map(function (p) {
            return p.name;
          });
        }
      },

      deep: true,
      immediate: true
    }
  }
};
</script>

<style scoped>
/* Dashboard Container */
.dashboard-container {
  padding: 10px;
}

/* Guidance Header */
.guidance-header-card {
  margin-bottom: 20px;
}

.guidance-title-section {
  display: inline-block;
  margin-left: 15px;
}

.status-badge {
  margin-left: 10px;
  font-size: 12px;
  vertical-align: middle;
}

.guidance-name {
  color: #666;
  margin-top: 5px;
  font-size: 14px;
}

/* Quick Stats */
.quick-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 15px;
}

.stat-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f5f5f5;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
}

.stat-chip md-icon {
  font-size: 18px;
  color: #9c27b0;
}

/* Key Metrics */
.key-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.metric-card {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-radius: 10px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.metric-icon md-icon {
  font-size: 36px;
}

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 20px;
  font-weight: bold;
  color: #333;
}

.metric-label {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

/* Statistics Card */
.statistics-card {
  margin-bottom: 20px;
}

.card-header-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: bold;
}

/* Day Type Stats */
.day-type-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
}

.day-type-card {
  padding: 15px;
  border-radius: 10px;
  text-align: center;
  color: white;
}

.day-type-card.weekday-card {
  background: linear-gradient(135deg, #4caf50, #81c784);
}

.day-type-card.weekend-card {
  background: linear-gradient(135deg, #ff9800, #ffb74d);
}

.day-type-card.holiday-card {
  background: linear-gradient(135deg, #f44336, #ef5350);
}

.day-type-header {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
}

.day-type-value {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 5px;
}

.day-type-label {
  font-size: 11px;
  opacity: 0.9;
}

.day-type-sub {
  font-size: 10px;
  opacity: 0.8;
  margin-top: 5px;
}

/* Status Card */
.status-card, .history-card, .actions-card {
  margin-bottom: 20px;
}

.status-progress {
  margin-bottom: 10px;
}

.progress-step {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  position: relative;
}

.progress-step:not(:last-child):before {
  content: '';
  position: absolute;
  left: 12px;
  top: 35px;
  bottom: -15px;
  width: 2px;
  background: #e0e0e0;
}

.progress-step.completed .step-marker md-icon {
  color: #4caf50;
}

.progress-step.active .step-marker md-icon {
  color: #2196f3;
}

.progress-step.pending .step-marker md-icon {
  color: #bdbdbd;
}

.step-content {
  flex: 1;
}

.step-name {
  font-weight: 500;
  font-size: 14px;
}

.step-date {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
}

/* Status History */
.status-history {
  max-height: 300px;
  overflow-y: auto;
}

.history-item {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.history-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  min-width: 80px;
  text-align: center;
}

.badge-info {
  background: #e3f2fd;
  color: #1976d2;
}

.badge-danger {
  background: #ffebee;
  color: #d32f2f;
}

.history-details {
  flex: 1;
}

.history-reason {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.history-meta {
  font-size: 10px;
  color: #999;
}

.history-date {
  margin-left: 8px;
}

.no-history {
  text-align: center;
  padding: 30px;
  color: #999;
}

/* Action Buttons Vertical */
.action-buttons-vertical {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.md-button.md-block {
  display: flex;
  width: 100%;
  margin: 0;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

/* Work Points */
.workpoints-container {
  max-height: 600px;
  overflow-y: auto;
}

.workpoint-card {
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
}

.workpoint-header {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: #f9f9f9;
}

.workpoint-icon md-icon {
  font-size: 28px;
  color: #9c27b0;
}

.workpoint-title {
  flex: 1;
  margin: 0;
}

.workpoint-stats .stat-badge {
  display: flex;
  align-items: center;
  gap: 5px;
  background: #e3f2fd;
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 13px;
}

.exception-icon-small {
  font-size: 16px;
  color: #ff9800;
}

.subtotal-row {
  background-color: #f5f5f5;
  font-weight: bold;
}

.empty-activities {
  text-align: center;
  padding: 40px;
  color: #999;
}

/* Exceptions Summary */
.exceptions-summary {
  padding: 15px;
}

.exception-group {
  margin-bottom: 25px;
}

.exception-group h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
  color: #ff9800;
}

.no-exceptions {
  text-align: center;
  padding: 40px;
  color: #4caf50;
}

.no-exceptions md-icon {
  font-size: 48px;
  width: 48px;
  height: 48px;
}

/* Print Styles */
@media print {
  .no-print {
    display: none !important;
  }

  .print-btn {
    display: none;
  }

  .print-container {
    padding: 0;
    margin: 0;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .key-metrics {
    grid-template-columns: 1fr 1fr;
  }

  .quick-stats {
    flex-direction: column;
  }

  .day-type-stats {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
